/**
 * Resolve the user's preferred locale via the canonical chain:
 *   1. `navigator.languages[0]` — most-preferred entry of the user's
 *      language list (Chrome, Firefox honour the OS-level ordering)
 *   2. `navigator.language` — single fallback for engines that don't
 *      populate `languages` (rare on modern browsers, common on
 *      `node:test` / jsdom-light test runners)
 *   3. `'en-GB'` — project-wide fallback. EU-targeted dashboard,
 *      English-but-not-US default keeps date/number formatting closer
 *      to the audit-grade register the rest of the dashboard renders.
 *
 * Resolved per-call rather than cached at module load because:
 *   - SSR safety (`navigator` is undefined under Node)
 *   - test isolation (jsdom resets `navigator` between suites)
 *   - language-switcher flexibility (if Adjudon ever ships an in-app
 *     locale toggle, a module-cached value would freeze the first
 *     resolution forever)
 *
 * Performance: the function is trivially cheap; consumers wrap the
 * call in `useMemo(() => resolveLocale(), [])` to share the resolved
 * string across a component's hot paths.
 */
export function resolveLocale(): string {
  if (typeof navigator === 'undefined') return 'en-GB';
  const langs = Array.isArray(navigator.languages) ? navigator.languages : null;
  if (langs && langs.length > 0 && typeof langs[0] === 'string' && langs[0].length > 0) {
    return langs[0];
  }
  const lang = navigator.language;
  return typeof lang === 'string' && lang.length > 0 ? lang : 'en-GB';
}
